<template>
  <VueFinalModal
    v-model="isOpen"
    content-transition="vfm-fade"
    :click-to-close="true"
    class="flex justify-center items-center p-6"
    content-class="relative w-full max-w-6xl h-[85vh] bg-[#fafbfc] rounded-[40px] shadow-2xl flex overflow-hidden z-50 ring-1 ring-black/5"
    @close="close"
  >
    <div class="flex w-full h-full min-h-0">
      <div class="w-full lg:w-[32%] bg-[#1c4848] relative flex flex-col p-8 text-white shrink-0 overflow-hidden">
        <div class="relative z-10 flex items-center gap-3 mb-6 opacity-80">
          <div class="w-8 h-8 rounded-full bg-white/10 border border-white/10 flex items-center justify-center text-[#ecd79f]">
            <IconMemoryGame class="w-[18px] h-[18px]" />
          </div>
          <span class="text-xs font-bold tracking-[0.2em] uppercase">Preview</span>
        </div>
        <div class="relative z-10 flex-1 flex items-center justify-center">
          <div class="relative w-[260px] h-[520px] bg-white rounded-[40px] border-[8px] border-[#2d3748] shadow-2xl flex flex-col overflow-hidden">
            <div class="absolute top-0 inset-x-0 h-6 bg-[#2d3748] z-20 flex justify-center">
              <div class="w-20 h-4 bg-[#1a202c] rounded-b-xl"></div>
            </div>
            <div class="flex-1 bg-[#fafbfc] w-full pt-8 px-3 pb-2 flex flex-col min-h-0">
              <div class="flex justify-between items-center mb-2 shrink-0">
                <div class="text-[#1c4848] font-bold text-sm">Pairs</div>
                <div class="bg-[#ecd79f] px-2 py-1 rounded text-[10px] font-bold text-[#1c4848]">00:00</div>
              </div>
              <div
                class="grid gap-1.5 w-full"
                :style="{ gridTemplateColumns: `repeat(${memoryPreviewCols}, minmax(0, 1fr))` }"
              >
                <div
                  v-for="i in memoryPreviewTotal"
                  :key="i"
                  class="aspect-square rounded-lg shadow-sm w-full"
                  :class="memoryPreviewFlippedIndices.includes(i) ? 'bg-[#ecd79f] flex items-center justify-center text-[#1c4848] font-bold text-lg' : 'bg-[#1c4848]'"
                >
                  {{ memoryPreviewFlippedIndices.includes(i) ? '?' : '' }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="relative z-10 mt-auto pt-8 border-t border-white/10 text-white/30">
          <p class="text-[9px] font-bold uppercase tracking-widest">Grid Layout</p>
          <p class="text-[10px] font-mono mt-0.5 text-[#ecd79f]">{{ memoryPreviewLabel }} (Square Tiles)</p>
        </div>
      </div>

      <div class="flex-1 flex flex-col bg-[#fafbfc] relative overflow-hidden min-w-0">
        <header class="px-8 py-6 border-b border-[#1c4848]/5 flex items-center justify-between bg-white/50 backdrop-blur-sm sticky top-0 z-30">
          <h2 class="text-xl font-bold text-[#1c4848] tracking-tight">Create Memory Game</h2>
          <div class="flex gap-3">
            <button type="button" class="px-4 py-2 rounded-xl text-xs font-bold text-[#1c4848]/60 hover:text-[#1c4848] hover:bg-[#1c4848]/5 transition-colors" @click="close">Cancel</button>
            <button type="button" :disabled="!canSave" class="px-5 py-2 rounded-xl text-xs font-bold text-white bg-[#1c4848] shadow-lg shadow-[#1c4848]/20 hover:bg-[#153838] active:scale-95 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" @click="save">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
              Save Game
            </button>
          </div>
        </header>

        <div class="flex-1 overflow-y-auto custom-scrollbar p-8">
          <form class="space-y-8 max-w-3xl mx-auto" @submit.prevent="save">
            <section>
              <h4 class="text-xs font-bold text-[#1c4848] uppercase tracking-widest mb-4">General Information</h4>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div class="md:col-span-2">
                  <TextInput v-model="form.name" label="Game Title" placeholder="e.g. Prophets Matching" input-id="memory-title" />
                </div>
                <div class="md:col-span-2">
                  <TextareaInput v-model="form.description" label="Description" placeholder="Briefly explain what to match..." :rows="3" textarea-id="memory-desc" />
                </div>
                <div class="flex flex-col">
                  <label class="block text-[10px] font-bold text-[#1c4848]/60 uppercase tracking-wider mb-1.5 ml-1">Difficulty</label>
                  <DifficultyBadge :difficulty="suggestedDifficulty" />
                  <span class="text-[10px] text-[#1c4848]/40 mt-1">From grid & flip speed</span>
                </div>
                <div class="flex flex-col">
                  <label class="block text-[10px] font-bold text-[#1c4848]/60 uppercase tracking-wider mb-1.5 ml-1">Grid Size</label>
                  <select v-model="form.grid_size" class="w-full appearance-none bg-white border border-[#1c4848]/10 rounded-2xl px-4 py-3 text-sm font-medium text-[#1c4848] focus:outline-none focus:border-[#1c4848]/30 focus:ring-4 focus:ring-[#1c4848]/5 shadow-sm cursor-pointer">
                    <option value="3x4">3×4 (12 Cards)</option>
                    <option value="4x4">4×4 (16 Cards)</option>
                    <option value="4x6">4×6 (24 Cards)</option>
                  </select>
                </div>
                <div>
                  <SelectInput v-model="form.languageId" label="Language" select-id="memory-language" :options="languageOptions" />
                </div>
              </div>
            </section>

            <section>
              <h4 class="text-xs font-bold text-[#1c4848] uppercase tracking-widest mb-4">Visual Assets</h4>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-5 md:col-span-2">
                  <UploadImageSizeSelect
                    v-model="form.uploadSizeCover"
                    select-id="create-memory-cover-size"
                    label="Cover image size"
                    hint="Listing / preview — often M or XL."
                  />
                  <UploadImageSizeSelect
                    v-model="form.uploadSizePairs"
                    select-id="create-memory-pairs-size"
                    label="Pair card images size"
                    hint="In-game tiles — often XS or S."
                  />
                </div>
                <div class="w-full md:col-span-2">
                  <label class="block text-[10px] font-bold text-[#1c4848]/60 uppercase tracking-wider mb-1.5 ml-1">Cover Image</label>
                  <div class="w-full h-40">
                    <ImageDropzone v-model="form.coverFile" input-id="memory-cover" size="small" class="h-full w-full" />
                  </div>
                </div>
                <div class="md:col-span-2">
                  <label class="block text-[10px] font-bold text-[#1c4848]/60 uppercase tracking-wider mb-1.5 ml-1">Pair Images (PNG/JPG)</label>
                  <input ref="pairsInputRef" type="file" class="hidden" accept=".png,.jpg,.jpeg,image/*" multiple @change="onPairsChange">
                  <label
                    for="memory-pairs"
                    class="flex items-center gap-6 p-4 rounded-3xl border-2 border-dashed border-[#1c4848]/10 bg-white hover:bg-[#1c4848]/[0.02] hover:border-[#1c4848]/30 transition-all cursor-pointer"
                    @click.prevent="pairsInputRef?.click()"
                  >
                    <div class="w-16 h-16 rounded-xl bg-gray-50 flex items-center justify-center text-[#1c4848]/30">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
                    </div>
                    <div class="flex-1">
                      <h5 class="text-sm font-bold text-[#1c4848] mb-1">Upload Pair Images</h5>
                      <p class="text-xs text-[#1c4848]/50">{{ form.assetFiles?.length ? `${form.assetFiles.length} file(s) selected` : 'Recommended 400x400px. Transparent PNGs work best.' }}</p>
                      <span class="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#1c4848]/5 text-[10px] font-bold text-[#1c4848]/70 mt-2">Select Files</span>
                    </div>
                  </label>
                </div>
              </div>
            </section>

            <section>
              <h4 class="text-xs font-bold text-[#1c4848] uppercase tracking-widest mb-4">Configuration</h4>
              <div class="bg-white rounded-3xl p-6 border border-[#1c4848]/5 shadow-sm grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-[9px] font-bold text-[#1c4848]/40 uppercase tracking-wider mb-1.5">Flip Speed</label>
                  <select v-model="form.flip_speed" class="w-full bg-[#f2f4f4] border-none rounded-xl px-4 py-2.5 text-xs font-bold text-[#1c4848] focus:ring-2 focus:ring-[#1c4848]/10 outline-none">
                    <option value="normal">Normal (0.5s)</option>
                    <option value="fast">Fast (0.3s)</option>
                    <option value="slow">Slow (1.0s)</option>
                  </select>
                </div>
                <div class="md:col-span-2">
                  <label class="block text-[9px] font-bold text-[#1c4848]/40 uppercase tracking-wider mb-1.5">Win Message</label>
                  <input v-model="form.win_message" type="text" class="w-full bg-[#f2f4f4] border-none rounded-xl px-4 py-2.5 text-xs font-bold text-[#1c4848] focus:ring-2 focus:ring-[#1c4848]/10 outline-none" placeholder="Mashallah! Great Job!">
                </div>
              </div>
            </section>
          </form>
        </div>
      </div>
    </div>
  </VueFinalModal>
</template>

<script setup>
import { ref, computed, reactive, watch } from 'vue';
import { VueFinalModal } from 'vue-final-modal';

function parseMemoryGridSize(gridSize) {
  const s = (gridSize || '3x4').toString();
  const [c, r] = s.split('x').map(Number);
  const cols = c && !Number.isNaN(c) ? c : 3;
  const rows = r && !Number.isNaN(r) ? r : 4;
  return { cols, rows, total: cols * rows };
}
import TextInput from '@/components/forms/text/TextInput.vue';
import TextareaInput from '@/components/forms/textarea/TextareaInput.vue';
import SelectInput from '@/components/forms/select/SelectInput.vue';
import UploadImageSizeSelect from '@/components/forms/select/UploadImageSizeSelect.vue';
import ImageDropzone from '@/components/forms/dropzone/ImageDropzone.vue';
import DifficultyBadge from '@/components/app/dashboard/DifficultyBadge.vue';
import IconMemoryGame from '@/components/app/dashboard/games/icons/IconMemoryGame.vue';
import { useGamesStore } from '@/store/games/index.js';
import { useLanguagesStore } from '@/store/languages/index.js';
import { getLanguageLabel } from '@/utils/languageLabel.js';

const props = defineProps({ modelValue: { type: Boolean, default: false } });
const emit = defineEmits(['update:modelValue', 'close', 'saved']);
const isOpen = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
});

const gamesStore = useGamesStore();
const languagesStore = useLanguagesStore();
const pairsInputRef = ref(null);

const form = reactive({
  name: '',
  description: '',
  difficulty: 1,
  grid_size: '3x4',
  flip_speed: 'normal',
  win_message: 'Mashallah! Great Job!',
  languageId: '',
  uploadSizeCover: '',
  uploadSizePairs: '',
  coverFile: null,
  assetFiles: [],
});

const languageOptions = computed(() => {
  const list = languagesStore.languages ?? [];
  return [{ value: '', label: 'Select language' }, ...list.map((l) => ({ value: String(l.id), label: getLanguageLabel(l) }))];
});

const memoryPreview = computed(() => parseMemoryGridSize(form.grid_size));
const memoryPreviewCols = computed(() => memoryPreview.value.cols);
const memoryPreviewTotal = computed(() => memoryPreview.value.total);
const memoryPreviewLabel = computed(() => {
  const { cols, rows } = memoryPreview.value;
  return `${cols} × ${rows}`;
});
const memoryPreviewFlippedIndices = computed(() => {
  const total = memoryPreviewTotal.value;
  if (total <= 2) return [1, 2];
  return [2, Math.floor(total / 2) + 1];
});

const suggestedDifficulty = computed(() => {
  const total = memoryPreviewTotal.value;
  const gridBase = total <= 12 ? 1 : total <= 16 ? 2 : 3;
  const flipAdjust = form.flip_speed === 'slow' ? -1 : form.flip_speed === 'fast' ? 1 : 0;
  return Math.max(1, Math.min(3, gridBase + flipAdjust));
});

watch(() => props.modelValue, (v) => { if (v) languagesStore.fetchLanguages(); });

watch(
  () => [form.grid_size, form.flip_speed],
  () => {
    form.difficulty = suggestedDifficulty.value;
  },
  { immediate: true }
);

const canSave = computed(() => {
  return form.name?.trim() && form.languageId && form.coverFile instanceof File && Array.isArray(form.assetFiles) && form.assetFiles.length > 0 && form.assetFiles.every((f) => f instanceof File);
});

function onPairsChange(e) {
  const files = e.target?.files;
  form.assetFiles = files ? Array.from(files).filter((f) => f.type.startsWith('image/')) : [];
}

function close() {
  emit('update:modelValue', false);
  emit('close');
}

async function save() {
  if (!canSave.value) return;
  try {
    const payload = {
      name: form.name.trim(),
      description: form.description?.trim() || null,
      game_type: 'match_pairs',
      difficulty: form.difficulty,
      language_id: form.languageId ? Number(form.languageId) : null,
      config: {
        grid_size: form.grid_size,
        flip_speed: form.flip_speed,
        win_message: form.win_message,
      },
      rating: null,
    };
    const created = await gamesStore.create(payload);
    await gamesStore.uploadCover(created.id, form.coverFile, form.uploadSizeCover);
    await gamesStore.uploadAssets(created.id, form.assetFiles, true, form.uploadSizePairs);
    emit('saved');
    close();
  } catch (err) {
    console.error(err);
    alert('Failed to save. Please try again.');
  }
}
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar { width: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(28, 72, 72, 0.2); border-radius: 3px; }
</style>
